import type { PasswordOptions } from './generatePassword';

export interface PasswordValidationError {
  message: string;
  args: Record<string, number>;
}

/**
 * Validates a password against the specified options.
 *
 * @param {string} password - The password to validate.
 * @param {PasswordOptions} options - The options to validate against.
 * @returns {PasswordValidationError | undefined} - An error message and args if invalid, or undefined if valid.
 */
export const validatePassword = (password: string, options: PasswordOptions): PasswordValidationError | undefined => {
  if (!password) return undefined;

  if (password.length < options.minLength) {
    return {
      message: 'resources.service_classes.errors.password_policy.minLength',
      args: { minLength: options.minLength },
    };
  }
  if (password.length > options.maxLength) {
    return {
      message: 'resources.service_classes.errors.password_policy.maxLength',
      args: { maxLength: options.maxLength },
    };
  }

  const uppercaseCount = (password.match(/[A-Z]/g) ?? []).length;
  if (uppercaseCount < options.minUppercase) {
    return {
      message: 'resources.service_classes.errors.password_policy.minUppercase',
      args: { minUppercase: options.minUppercase },
    };
  }

  const lowercaseCount = (password.match(/[a-z]/g) ?? []).length;
  if (lowercaseCount < options.minLowercase) {
    return {
      message: 'resources.service_classes.errors.password_policy.minLowercase',
      args: { minLowercase: options.minLowercase },
    };
  }

  const numberCount = (password.match(/[0-9]/g) ?? []).length;
  if (numberCount < options.minNumbers) {
    return {
      message: 'resources.service_classes.errors.password_policy.minNumbers',
      args: { minNumbers: options.minNumbers },
    };
  }

  const specialChars = '!@#$%^&*()-=_+[]{}|;:,.<>?';
  const specialCount = password.split('').filter((char) => specialChars.includes(char)).length;
  if (specialCount < options.minSpecials) {
    return {
      message: 'resources.service_classes.errors.password_policy.minSpecials',
      args: { minSpecials: options.minSpecials },
    };
  }

  return undefined;
};
